import styled from 'styled-components';
import { IconGraphy, Table, Tooltip } from '@leapeasy/ui-kit';
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { getDemoData } from 'utils/helpers';
import { ListExportScopeModal } from '../shared/ListExportScopeModal';
import { GlobalUXState } from 'components/GlobalUXState';
import {
  fitContentCompactColumnOptions,
  fitContentTextColumnOptions,
} from 'utils/fitContentTextColumnOptions';
import {
  LANDLORD_TABLE_DEFAULT_VISIBLE_KEYS,
  LANDLORD_TABLE_STORAGE_KEY,
} from './landlordTableConfig';

const LEAP_PURPLE = '#702572';
const DISABLED_ICON_COLOR = '#B8B0BC';

const dateColumnOptions = (overrides = {}) => ({
  width: 110,
  shrink: false,
  ...overrides,
});

const formatLandlordAddress = (landlord) =>
  [
    landlord.billingStreet || '',
    landlord.billingCity || '',
    landlord.billingState || '',
    landlord.billingPostalCode || '',
  ]
    .join(' ')
    .trim();

export const LandlordTable = forwardRef(
  (
    {
      landlords,
      filter,
      setSelectedLandlord,
      dropFilter,
      onChangeFilter,
      pagination,
      onChangePagination,
      sortOptions,
      onChangeSort,
      onClickLandlord,
      onRequestExportData,
      onRequestFullExportData,
      exportFullDatasetTotal,
      dropFilterActiveCount,
      dropFilterActiveSummary,
    },
    ref,
  ) => {
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const tableRef = useRef(null);
    const isDemo = useSelector((state) => state.getIn(['ui', 'demo']));

    useImperativeHandle(ref, () => ({
      exportCsv: (...args) => tableRef.current?.exportCsv?.(...args),
    }));

    const buildLandlordRow = useCallback(
      (landlord, index) => {
        const address = formatLandlordAddress(landlord);

        return [
          isDemo ? getDemoData('landlord-name') : landlord.name,
          isDemo ? getDemoData('primary-contact') : landlord.primary_contact,
          isDemo ? getDemoData('email') : landlord.email_address,
          landlord.total_buildings ? landlord.total_buildings : 0,
          landlord.total_units ? landlord.total_units : 0,
          new Date(landlord.sf_createdDate).toISOString().slice(0, 10),
          isDemo ? getDemoData('address') : address,
          isDemo ? getDemoData('phone') : landlord.phone,
          index,
        ];
      },
      [isDemo],
    );

    const tableData = useMemo(() => {
      if (!landlords) return [];
      return landlords.map((landlord, index) => buildLandlordRow(landlord, index));
    }, [buildLandlordRow, landlords]);

    const goDetailPage = useCallback(
      (rowIndex) => {
        const landlord = landlords?.[rowIndex];
        if (landlord) {
          onClickLandlord(landlord);
        }
      },
      [landlords, onClickLandlord],
    );

    const columns = useMemo(
      () => [
        {
          name: 'Operator Name',
          key: 'name',
          options: {
            ...fitContentTextColumnOptions({ fitContentCharWidth: 9, maxWidth: 520, flexGrow: 1 }),
            sort: true,
          },
        },
        {
          name: 'Primary Contact',
          key: 'primary_contact',
          options: {
            ...fitContentTextColumnOptions({ fitContentCharWidth: 8, maxWidth: 280, flexGrow: 1 }),
            sort: true,
          },
        },
        {
          name: 'Email Address',
          key: 'email_address',
          options: {
            ...fitContentTextColumnOptions({ fitContentCharWidth: 10, maxWidth: 320, flexGrow: 1 }),
            sort: true,
          },
        },
        {
          name: 'Property Count',
          key: 'total_buildings',
          options: {
            ...fitContentCompactColumnOptions({ minWidth: 96, maxWidth: 120, align: 'center' }),
            sort: true,
          },
        },
        {
          name: 'Unit Count',
          key: 'total_units',
          options: {
            ...fitContentCompactColumnOptions({ minWidth: 72, maxWidth: 96, align: 'center' }),
            sort: true,
          },
        },
        {
          name: 'Create date',
          key: 'sf_createdDate',
          options: {
            ...dateColumnOptions(),
            sort: true,
          },
        },
        {
          name: 'Address',
          key: 'billingStreet',
          options: {
            ...fitContentTextColumnOptions({ minWidth: 120, maxWidth: 360 }),
            sort: true,
          },
        },
        {
          name: 'Phone Number',
          key: 'phone',
          options: {
            ...fitContentTextColumnOptions({ minWidth: 120, maxWidth: 180 }),
            sort: true,
          },
        },
        {
          name: 'Actions',
          key: 'actions',
          options: {
            width: 80,
            shrink: false,
            sort: false,
            align: 'center',
            excludeFromExport: true,
            stickyRight: true,
            customBodyRenderer: (rowIndex) => {
              const landlord = landlords?.[rowIndex];
              const landlordId = landlord?.sf_landlord_id;
              const canViewDetails = Boolean(landlord);
              const canDelete = Boolean(landlordId);

              return (
                <ActionsCell onClick={(e) => e.stopPropagation()}>
                  <ActionIconButton
                    icon="FileFolder.Description"
                    title="View operator details"
                    disabled={!canViewDetails}
                    onClick={() => goDetailPage(rowIndex)}
                  />
                  <ActionIconButton
                    icon="General.Delete"
                    title="Delete operator"
                    disabled={!canDelete}
                    onClick={() => {
                      if (canDelete) {
                        setSelectedLandlord(landlordId);
                      }
                    }}
                  />
                </ActionsCell>
              );
            },
          },
        },
      ],
      [goDetailPage, landlords, setSelectedLandlord],
    );

    return (
      <>
        <Table
          showEmptyState
          emptyContent={
            <GlobalUXState
              variant={filter?.searchText ? 'noResults' : 'noData'}
              compact
              description={
                filter?.searchText
                  ? 'No operators match your search.'
                  : 'No operators available yet.'
              }
            />
          }
          ref={tableRef}
          onExportButtonClick={() => setExportModalOpen(true)}
          autoTooltipForDefaultCells
          defaultVisibleColumnKeys={LANDLORD_TABLE_DEFAULT_VISIBLE_KEYS}
          columnVisibilityStorageKey={LANDLORD_TABLE_STORAGE_KEY}
          columnWidthStorageKey={LANDLORD_TABLE_STORAGE_KEY}
          onRequestExportData={
            onRequestExportData
              ? async () => {
                  const rows = await onRequestExportData();
                  return (rows || []).map((landlord, index) => buildLandlordRow(landlord, index));
                }
              : undefined
          }
          onRequestFullExportData={
            onRequestFullExportData
              ? async () => {
                  const rows = await onRequestFullExportData();
                  return (rows || []).map((landlord, index) => buildLandlordRow(landlord, index));
                }
              : undefined
          }
          data={tableData}
          filter={filter}
          dropFilter={dropFilter}
          dropFilterActiveCount={dropFilterActiveCount}
          dropFilterActiveSummary={dropFilterActiveSummary}
          onChangeFilter={onChangeFilter}
          onChangeRowsPerPage={(value) =>
            onChangePagination({
              ...pagination,
              rowsPerPage: value,
              pageNumber: 0,
            })
          }
          pagination={pagination}
          sortOptions={sortOptions}
          onChangeSort={onChangeSort}
          style={{ width: '100%' }}
          title="Operator"
          columns={columns}
        />
        <ListExportScopeModal
          open={exportModalOpen}
          onClose={() => setExportModalOpen(false)}
          tableRef={tableRef}
          title="Operator"
          currentPageRowCount={tableData.length}
          paginationTotalItems={pagination.totalItems}
          fullDatasetTotal={exportFullDatasetTotal}
          enableFiltered={Boolean(onRequestExportData)}
          enableFull={Boolean(onRequestFullExportData)}
          enableSelected={false}
        />
      </>
    );
  },
);

LandlordTable.displayName = 'LandlordTable';

const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  width: 100%;
`;

const ActionIconButton = ({ icon, title, disabled, onClick }) => (
  <Tooltip title={title} placement="top" showCarot>
    <span>
      <ActionIcon
        type="button"
        disabled={disabled}
        aria-label={title}
        onClick={disabled ? undefined : onClick}
      >
        <IconGraphy icon={icon} style={{ color: disabled ? DISABLED_ICON_COLOR : LEAP_PURPLE }} />
      </ActionIcon>
    </span>
  </Tooltip>
);

const ActionIcon = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  line-height: 0;

  &:not(:disabled) {
    cursor: pointer;
  }

  &:disabled {
    cursor: default;
    pointer-events: none;
  }
`;
